import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Text,
    TextInput,
    Radio,
    Checkbox,
    Button,
    Group,
    Stack,
    Flex,
    Box,
    Divider
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useRequest } from 'ahooks';
import { useStore } from '../../../hooks/use-store';
import { NOTIFICATION_SERVICE } from '../../../components/notifications/NotificationService';
import Logo from '../../../components/Logo';
import { Pricing } from '../pricing/Pricing';

interface QuestionnaireFormData {
    fullName: string;
    companyName: string;
    companySize: string;
    visualTypes: string[];
    visualsPerYear: string;
    challenges: string[];
}

const Questionnaire = () => {
    const {
        uiStore: { authStore },
        dataStore: { appStore },
    } = useStore();

    const [showPricing, setShowPricing] = useState<boolean>(true);
    const [submissionState, setSubmissionState] = useState<'form' | 'qualified' | 'disqualified' | 'demo_scheduled'>('form');
    const [setupLink, setSetupLink] = useState<string>('');

    // Generate Calendly URL with proper embed parameters
    const getCalendlyUrl = () => {
        const baseUrl = 'https://calendly.com/maground_com/ai-demo';
        const currentDomain = window.location.hostname;
        const params = new URLSearchParams({
            embed_domain: currentDomain,
            embed_type: 'Inline',
            hide_event_type_details: '1',
            hide_gdpr_banner: '1'
        });
        return `${baseUrl}?${params.toString()}`;
    };

    // Listen for Calendly events
    useEffect(() => {
        const handleCalendlyEvent = async (e: MessageEvent) => {
            // Debug logging - log all messages to understand what's being received
            console.log('Received postMessage:', {
                origin: e.origin,
                data: e.data,
                source: e.source
            });

            // Check if message is from Calendly
            if (!e.origin.includes('calendly.com')) {
                console.log('Message not from Calendly, ignoring');
                return;
            }

            // Handle different Calendly event formats
            let eventType = null;

            // Format 1: e.data.event === 'calendly.event_scheduled'
            if (e.data && e.data.event === 'calendly.event_scheduled') {
                eventType = 'event_scheduled';
            }

            // Format 2: e.data === 'calendly.event_scheduled'
            if (e.data === 'calendly.event_scheduled') {
                eventType = 'event_scheduled';
            }

            // Format 3: e.data.type === 'event_scheduled'
            if (e.data && e.data.type === 'event_scheduled') {
                eventType = 'event_scheduled';
            }

            // Format 4: Nested event object
            if (e.data && e.data.payload && e.data.payload.event === 'calendly.event_scheduled') {
                eventType = 'event_scheduled';
            }

            console.log('Detected event type:', eventType);

            if (eventType === 'event_scheduled') {
                console.log('Processing Calendly event_scheduled');
                
                // Send demo scheduled event to Intercom
                if (window?.Intercom) {
                    try {
                        window.Intercom('update', {
                            'Demo Scheduled': true,
                            'Demo Scheduled Date': new Date().toISOString(),
                        });
                        console.log('Demo scheduled event sent to Intercom successfully');
                    } catch (error) {
                        console.error('Failed to send demo scheduled event to Intercom:', error);
                    }
                }
                
                // Demo was scheduled, show card setup message
                try {
                    const setupResponse = await appStore.getSetupLink();
                    setSetupLink(setupResponse.url || '');
                    setSubmissionState('demo_scheduled');
                    NOTIFICATION_SERVICE.successNotification('Demo scheduled successfully!');
                } catch (error) {
                    console.error('Failed to get setup link:', error);
                    setSubmissionState('demo_scheduled');
                    NOTIFICATION_SERVICE.successNotification('Demo scheduled successfully!');
                }
            }
        };

        console.log('Setting up Calendly event listener');
        window.addEventListener('message', handleCalendlyEvent);

        return () => {
            console.log('Removing Calendly event listener');
            window.removeEventListener('message', handleCalendlyEvent);
        };
    }, [appStore]);

    const form = useForm<QuestionnaireFormData>({
        initialValues: {
            fullName: '',
            companyName: '',
            companySize: '',
            visualTypes: [],
            visualsPerYear: '',
            challenges: [],
        },
        validate: {
            fullName: (value) => value.length < 2 ? 'Name must contain at least 2 characters' : null,
            companyName: (value) => value.length < 2 ? 'Company name must contain at least 2 characters' : null,
            companySize: (value) => !value ? 'Please select company size' : null,
            visualTypes: (value) => value.length === 0 ? 'Please select at least one visual type' : null,
            visualsPerYear: (value) => !value ? 'Please select number of visuals per year' : null,
            challenges: (value) => value.length === 0 ? 'Please select at least one challenge' : null,
        },
    });

    const { loading: submissionLoading, runAsync: submitQuestionnaire } = useRequest(
        async (data: QuestionnaireFormData) => {
            // Determine qualification status
            const isSmallCompany = data.companySize === '1-10';
            const isLowVolume = data.visualsPerYear === '1-5';
            const isDisqualified = isSmallCompany && isLowVolume;

            const submissionData = {
                ...data,
                email: authStore.currentUser?.email || '',
                qualified: !isDisqualified,
                submittedAt: new Date().toISOString(),
            };

            return appStore.sendQuestionnaire(submissionData);
        },
        {
            manual: true,
            onSuccess: async (response, [formData]) => {
                const isSmallCompany = formData.companySize === '1-10';
                const isLowVolume = formData.visualsPerYear === '1-5';
                const isDisqualified = isSmallCompany && isLowVolume;

                // Send questionnaire data to Intercom
                if (window?.Intercom) {
                    try {
                        window.Intercom('update', {
                            'Questionnaire Full Name': formData.fullName,
                            'Questionnaire Company Name': formData.companyName,
                            'Questionnaire Company Size': formData.companySize,
                            'Questionnaire Visual Types': formData.visualTypes.join(', '),
                            'Questionnaire Visuals Per Year': formData.visualsPerYear,
                            'Questionnaire Challenges': formData.challenges.join(', '),
                            'Questionnaire Qualified': !isDisqualified,
                            'Questionnaire Submission Date': new Date().toISOString(),
                        });
                        console.log('Questionnaire data sent to Intercom successfully');
                    } catch (error) {
                        console.error(
                        'Failed to send questionnaire data to Intercom:', error);
                    }
                }

                if (isDisqualified) {
                    // Get setup link for disqualified users
                    try {
                        const setupResponse = await appStore.getSetupLink();
                        setSetupLink(setupResponse.url || '');
                    } catch (error) {
                        console.error('Failed to get setup link:', error);
                    }
                    setSubmissionState('disqualified');
                } else {
                    setSubmissionState('qualified');
                }

                NOTIFICATION_SERVICE.successNotification('Questionnaire submitted successfully');
            },
            onError: (error) => {
                NOTIFICATION_SERVICE.errorNotification(
                    'Failed to Activate my free demo. Please try again.'
                );
                console.error('Questionnaire submission error:', error);
            },
        }
    );

    const handleSubmit = (values: QuestionnaireFormData) => {
        submitQuestionnaire(values);
    };

    const handleStartTrial = () => {
        setShowPricing(false);
    };

    const companyOptions = [
        { value: '1-10', label: '1–10' },
        { value: '11-50', label: '11–50' },
        { value: '51-200', label: '51–200' },
        { value: '201-1000', label: '201–1,000' },
        { value: '1000+', label: '1,000+' },
    ];

    const visualTypeOptions = [
        { value: 'dealer-campaigns', label: 'Dealer-level campaigns' },
        { value: 'national-campaigns', label: 'National campaigns' },
        { value: 'global-launches', label: 'Global launches / hero campaigns' },
        { value: 'internal-concepting', label: 'Internal concepting & pitch decks' },
    ];

    const visualsPerYearOptions = [
        { value: '1-5', label: '1–5' },
        { value: '6-15', label: '6–15' },
        { value: '16-30', label: '16–30' },
        { value: '30+', label: '30+' },
    ];

    const challengeOptions = [
        { value: 'expensive', label: 'Too expensive / high production cost' },
        { value: 'slow', label: 'Too slow / time to market' },
        { value: 'limited-flexibility', label: 'Limited flexibility (backgrounds, variants, adaptations)' },
        { value: 'workflow-management', label: 'Managing multiple agencies & workflows' },
    ];

    // Show pricing first
    if (showPricing) {
        return <Pricing private={!!authStore.currentUser} onStartTrial={handleStartTrial} />;
    }

    if (submissionState === 'disqualified') {
        return (
            <Container size="md">
                <Paper p={40} radius="md" style={{ background: 'rgba(16, 16, 16, 1)', border: '1px solid rgba(70, 70, 70, 1)' }}>
                    <Stack spacing="xl" align="center">
                        <Flex justify="center" mt="lg">
                            <Logo size={60} />
                        </Flex>
                        <Text size="xl" weight={700} color="white" align="center">
                            🎉 Your free demo is almost ready!
                        </Text>
                        <Text size="md" color="#BDBDBD" align="center" maw={500}>
                            To activate your demo account with 200 free credits, please add a card to your profile. 👉 No charges now — only if you decide to continue after 7 days.
                        </Text>
                        <Button
                            size="lg"
                            styles={{
                                root: {
                                    background: '#f53c02 !important',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '16px',
                                    height: '48px',
                                    paddingLeft: '32px',
                                    paddingRight: '32px',
                                    '&:hover': {
                                        background: '#d63384 !important',
                                    },
                                },
                            }}
                            onClick={() => {
                                if (setupLink) {
                                    window.open(setupLink, '_blank');
                                }
                            }}
                        >
                            Add Card & Start Demo
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (submissionState === 'demo_scheduled') {
        return (
            <Container size="md">
                <Paper p={40} radius="md" style={{ background: 'rgba(16, 16, 16, 1)', border: '1px solid rgba(70, 70, 70, 1)' }}>
                    <Stack spacing="xl" align="center">
                        <Flex justify="center" mt="lg">
                            <Logo size={60} />
                        </Flex>
                        <Text size="xl" weight={700} color="white" align="center">
                            🎉 Your free demo is almost ready!
                        </Text>
                        <Text size="md" color="#BDBDBD" align="center" maw={500}>
                            To activate your demo account with 200 free credits, please add a card to your profile. 👉 No charges now — only if you decide to continue after 7 days.
                        </Text>
                        <Button
                            size="lg"
                            styles={{
                                root: {
                                    background: '#f53c02 !important',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '16px',
                                    height: '48px',
                                    paddingLeft: '32px',
                                    paddingRight: '32px',
                                    '&:hover': {
                                        background: '#d63384 !important',
                                    },
                                },
                            }}
                            onClick={() => {
                                if (setupLink) {
                                    window.open(setupLink, '_blank');
                                }
                            }}
                        >
                            Add Card & Start Demo
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (submissionState === 'qualified') {
        return (
            <Container size="md">
                <Paper p={40} radius="md" style={{ background: 'rgba(16, 16, 16, 1)', border: '1px solid rgba(70, 70, 70, 1)' }}>
                    <Stack spacing="xl">
                        <Flex justify="center" mb="md">
                            <Logo size={60} />
                        </Flex>
                        <Text size="xl" weight={700} color="white" align="center">
                            Great! You qualify for our free demo + onboarding
                        </Text>
                        <Text size="md" color="#BDBDBD" align="center">
                            Please schedule your demo session below.
                        </Text>

                        {/* Debug button - remove in production */}
                        {process.env.NODE_ENV === 'development' && (
                            <Group position="center" mb="md">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        // Simulate Calendly event for testing
                                        window.postMessage(
                                            { event: 'calendly.event_scheduled' },
                                            window.location.origin
                                        );
                                    }}
                                    styles={{
                                        root: {
                                            borderColor: '#f53c02',
                                            color: '#f53c02',
                                            '&:hover': {
                                                backgroundColor: 'rgba(245, 60, 2, 0.1)',
                                            },
                                        },
                                    }}
                                >
                                    Test Demo Scheduled Flow (Dev Only)
                                </Button>
                            </Group>
                        )}

                        <Box>
                            <iframe
                                src={getCalendlyUrl()}
                                width="100%"
                                height="700"
                                frameBorder="0"
                                style={{ borderRadius: '8px' }}
                                allow="microphone; camera"
                                title="Schedule Demo"
                            ></iframe>
                        </Box>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="md">
            <Paper p={40} radius="md" style={{ background: 'rgba(16, 16, 16, 1)', border: '1px solid rgba(70, 70, 70, 1)' }}>
                <Stack spacing="xl">
                    <Flex justify="center" mb="md">
                        <Logo size={60} />
                    </Flex>
                    <Text size="xl" weight={700} color="white" align="center">
                        Unlock Your 7-Day Free MAGROUND.ai Demo
                    </Text>
                    <Text size="md" color="#BDBDBD" align="center" maw={600} mx="auto">
                        Experience the full power of MAGROUND.ai with unlimited access for 7 days — completely free.
                        Please fill out this short questionnaire to activate your free demo.
                        Your answers help us tailor the best setup for your team.
                    </Text>

                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <Stack spacing="xl">
                            {/* Personal Information */}
                            <Box>
                                <Text size="lg" weight={600} color="white" mb="md">
                                    Personal Information
                                </Text>
                                <Stack spacing="md">
                                    <TextInput
                                        required
                                        label="Full Name"
                                        placeholder="Enter your full name"
                                        styles={{
                                            label: {
                                                color: 'white',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                marginBottom: '8px',
                                            },
                                            input: {
                                                backgroundColor: 'rgba(32, 32, 32, 1)',
                                                border: '1px solid rgba(70, 70, 70, 1)',
                                                color: 'white',
                                                '&:focus': {
                                                    borderColor: '#f53c02',
                                                },
                                                '&::placeholder': {
                                                    color: '#9ca3af',
                                                },
                                            },
                                            error: {
                                                color: '#f53c02',
                                            },
                                        }}
                                        {...form.getInputProps('fullName')}
                                    />
                                    <TextInput
                                        required
                                        label="Company Name"
                                        placeholder="Enter your company name"
                                        styles={{
                                            label: {
                                                color: 'white',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                marginBottom: '8px',
                                            },
                                            input: {
                                                backgroundColor: 'rgba(32, 32, 32, 1)',
                                                border: '1px solid rgba(70, 70, 70, 1)',
                                                color: 'white',
                                                '&:focus': {
                                                    borderColor: '#f53c02',
                                                },
                                                '&::placeholder': {
                                                    color: '#9ca3af',
                                                },
                                            },
                                            error: {
                                                color: '#f53c02',
                                            },
                                        }}
                                        {...form.getInputProps('companyName')}
                                    />
                                </Stack>
                            </Box>

                            <Divider color="rgba(70, 70, 70, 1)" />

                            {/* Company Profile */}
                            <Box>
                                <Text size="lg" weight={600} color="white" mb="md">
                                    Company Profile
                                </Text>
                                <Stack spacing="md">
                                    <Box>
                                        <Text size="sm" weight={500} color="white" mb="xs">
                                            Company Size *
                                        </Text>
                                        <Radio.Group
                                            {...form.getInputProps('companySize')}
                                            styles={{
                                                error: {
                                                    color: '#f53c02',
                                                },
                                            }}
                                        >
                                            <Stack spacing="xs">
                                                {companyOptions.map((option) => (
                                                    <Radio
                                                        key={option.value}
                                                        value={option.value}
                                                        label={option.label}
                                                        styles={{
                                                            radio: {
                                                                '&:checked': {
                                                                    backgroundColor: '#f53c02',
                                                                    borderColor: '#f53c02',
                                                                },
                                                            },
                                                            label: {
                                                                color: 'white',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Radio.Group>
                                    </Box>
                                </Stack>
                            </Box>

                            <Divider color="rgba(70, 70, 70, 1)" />

                            {/* Work Scope */}
                            <Box>
                                <Text size="lg" weight={600} color="white" mb="md">
                                    Work Scope
                                </Text>
                                <Stack spacing="md">
                                    <Box>
                                        <Text size="sm" weight={500} color="white" mb="xs">
                                            What type of automotive visuals do you usually produce? *
                                        </Text>
                                        <Text size="xs" color="#9ca3af" mb="sm">
                                            Select all that apply
                                        </Text>
                                        <Checkbox.Group
                                            {...form.getInputProps('visualTypes')}
                                            styles={{
                                                error: {
                                                    color: '#f53c02',
                                                },
                                            }}
                                        >
                                            <Stack spacing="xs">
                                                {visualTypeOptions.map((option) => (
                                                    <Checkbox
                                                        key={option.value}
                                                        value={option.value}
                                                        label={option.label}
                                                        styles={{
                                                            input: {
                                                                '&:checked': {
                                                                    backgroundColor: '#f53c02',
                                                                    borderColor: '#f53c02',
                                                                },
                                                            },
                                                            label: {
                                                                color: 'white',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Checkbox.Group>
                                    </Box>

                                    <Box>
                                        <Text size="sm" weight={500} color="white" mb="xs">
                                            How many visuals are you going to produce per year? *
                                        </Text>
                                        <Radio.Group
                                            {...form.getInputProps('visualsPerYear')}
                                            styles={{
                                                error: {
                                                    color: '#f53c02',
                                                },
                                            }}
                                        >
                                            <Stack spacing="xs">
                                                {visualsPerYearOptions.map((option) => (
                                                    <Radio
                                                        key={option.value}
                                                        value={option.value}
                                                        label={option.label}
                                                        styles={{
                                                            radio: {
                                                                '&:checked': {
                                                                    backgroundColor: '#f53c02',
                                                                    borderColor: '#f53c02',
                                                                },
                                                            },
                                                            label: {
                                                                color: 'white',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Radio.Group>
                                    </Box>
                                </Stack>
                            </Box>

                            <Divider color="rgba(70, 70, 70, 1)" />

                            {/* Challenges */}
                            <Box>
                                <Text size="lg" weight={600} color="white" mb="md">
                                    Challenges
                                </Text>
                                <Stack spacing="md">
                                    <Box>
                                        <Text size="sm" weight={500} color="white" mb="xs">
                                            What is your biggest challenge today? *
                                        </Text>
                                        <Text size="xs" color="#9ca3af" mb="sm">
                                            Select all that apply
                                        </Text>
                                        <Checkbox.Group
                                            {...form.getInputProps('challenges')}
                                            styles={{
                                                error: {
                                                    color: '#f53c02',
                                                },
                                            }}
                                        >
                                            <Stack spacing="xs">
                                                {challengeOptions.map((option) => (
                                                    <Checkbox
                                                        key={option.value}
                                                        value={option.value}
                                                        label={option.label}
                                                        styles={{
                                                            input: {
                                                                '&:checked': {
                                                                    backgroundColor: '#f53c02',
                                                                    borderColor: '#f53c02',
                                                                },
                                                            },
                                                            label: {
                                                                color: 'white',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Checkbox.Group>
                                    </Box>
                                </Stack>
                            </Box>

                            <Divider color="rgba(70, 70, 70, 1)" />

                            {/* Submit Button */}
                            <Group position="center" mt="xl">
                                <Button
                                    type="submit"
                                    size="lg"
                                    loading={submissionLoading}
                                    styles={{
                                        root: {
                                            background: '#f53c02 !important',
                                            border: 'none',
                                            color: 'white',
                                            fontSize: '16px',
                                            height: '48px',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                            '&:hover': {
                                                background: '#d63384 !important',
                                            },
                                            '&:disabled': {
                                                background: 'rgba(70, 70, 70, 1) !important',
                                                color: '#9ca3af !important',
                                            },
                                        },
                                    }}
                                >
                                    Activate my free demo
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Stack>
            </Paper>
        </Container>
    );
};

export default Questionnaire;
